import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SeoRenderContext, type SeoRenderData } from "@/seo/SeoRenderContext";

const SITE_URL = "https://www.mercadoai.app";
const DEFAULT_SITE_NAME = "AfiliaBR";

type UsePageSeoOptions = {
  title: string;
  description: string;
  canonicalPath?: string;
  keywords?: string;
  robots?: string;
  image?: string;
  siteName?: string;
  ogType?: "website" | "article" | "product";
};

function ensureMetaTag(name: string) {
  let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }

  return element;
}

function ensurePropertyMetaTag(property: string) {
  let element = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }

  return element;
}

function ensureCanonicalTag() {
  let element = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  return element;
}

export function buildCanonicalUrl(path: string) {
  if (!path || path === "/") {
    return `${SITE_URL}/`;
  }

  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function resolveSeoData({
  title,
  description,
  canonicalPath,
  keywords,
  robots = "index,follow",
  image,
  siteName = DEFAULT_SITE_NAME,
  ogType = "website",
  pathname,
}: UsePageSeoOptions & { pathname: string }): SeoRenderData {
  const resolvedTitle = title.trim();
  const resolvedDescription = description.trim();
  const resolvedCanonicalPath = canonicalPath ?? pathname;
  const canonicalUrl = buildCanonicalUrl(resolvedCanonicalPath);
  const resolvedImage = image?.trim() || `${SITE_URL}/vite.svg`;
  const twitterCard = resolvedImage ? "summary_large_image" : "summary";

  return {
    title: resolvedTitle,
    description: resolvedDescription,
    canonicalUrl,
    keywords: keywords?.trim() || undefined,
    robots: robots.trim(),
    image: resolvedImage,
    siteName: siteName.trim() || DEFAULT_SITE_NAME,
    ogType,
    twitterCard,
  };
}

export function renderSeoTags(seo: SeoRenderData) {
  const escaped = {
    title: escapeHtml(seo.title),
    description: escapeHtml(seo.description),
    canonicalUrl: escapeHtml(seo.canonicalUrl),
    keywords: seo.keywords ? escapeHtml(seo.keywords) : "",
    robots: escapeHtml(seo.robots),
    image: escapeHtml(seo.image),
    siteName: escapeHtml(seo.siteName),
    ogType: escapeHtml(seo.ogType),
    twitterCard: escapeHtml(seo.twitterCard),
  };

  return [
    `<title>${escaped.title}</title>`,
    `<meta name="description" content="${escaped.description}" />`,
    `<meta name="robots" content="${escaped.robots}" />`,
    escaped.keywords ? `<meta name="keywords" content="${escaped.keywords}" />` : "",
    `<link rel="canonical" href="${escaped.canonicalUrl}" />`,
    `<meta property="og:type" content="${escaped.ogType}" />`,
    `<meta property="og:title" content="${escaped.title}" />`,
    `<meta property="og:description" content="${escaped.description}" />`,
    `<meta property="og:url" content="${escaped.canonicalUrl}" />`,
    `<meta property="og:image" content="${escaped.image}" />`,
    `<meta property="og:site_name" content="${escaped.siteName}" />`,
    `<meta property="og:locale" content="pt_BR" />`,
    `<meta name="twitter:card" content="${escaped.twitterCard}" />`,
    `<meta name="twitter:title" content="${escaped.title}" />`,
    `<meta name="twitter:description" content="${escaped.description}" />`,
    `<meta name="twitter:image" content="${escaped.image}" />`,
  ]
    .filter(Boolean)
    .join("\n");
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function usePageSeo(options: UsePageSeoOptions) {
  const location = useLocation();
  const seoCollector = useContext(SeoRenderContext);
  const seo = resolveSeoData({ ...options, pathname: location.pathname });

  if (seoCollector) {
    seoCollector.current = seo;
  }

  useEffect(() => {
    document.title = seo.title;
    ensureMetaTag("description").setAttribute("content", seo.description);
    ensureMetaTag("robots").setAttribute("content", seo.robots);
    ensureMetaTag("twitter:card").setAttribute("content", seo.twitterCard);
    ensureMetaTag("twitter:title").setAttribute("content", seo.title);
    ensureMetaTag("twitter:description").setAttribute("content", seo.description);
    ensureMetaTag("twitter:image").setAttribute("content", seo.image);

    if (seo.keywords) {
      ensureMetaTag("keywords").setAttribute("content", seo.keywords);
    }

    ensurePropertyMetaTag("og:type").setAttribute("content", seo.ogType);
    ensurePropertyMetaTag("og:title").setAttribute("content", seo.title);
    ensurePropertyMetaTag("og:description").setAttribute("content", seo.description);
    ensurePropertyMetaTag("og:url").setAttribute("content", seo.canonicalUrl);
    ensurePropertyMetaTag("og:image").setAttribute("content", seo.image);
    ensurePropertyMetaTag("og:site_name").setAttribute("content", seo.siteName);
    ensurePropertyMetaTag("og:locale").setAttribute("content", "pt_BR");

    ensureCanonicalTag().setAttribute("href", seo.canonicalUrl);
  }, [seo]);
}
